<script setup>
import { computed, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    order: {
        type: Object,
        required: true
    },
    stepIcons: {
        type: Object,
        default: () => ({})
    }
});

const itemsAnalysis = computed(() => {
    const items = props.order.orderItems || props.order.order_items || [];
    const totalItems = items.length;

    if (totalItems === 0) {
        return {
            totalItems: 0,
            hasContent: 0,
            contentConfirmed: 0,
            sentToPortal: 0,
            published: 0,
            rejected: 0,
            pendingContent: 0,
            allHaveContent: false,
            allContentConfirmed: false,
            allSentToPortal: false,
            allPublished: false,
            hasPendingItems: false,
            hasProcessableItems: false
        };
    }

    const hasContent = items.filter(item => item.content).length;
    const contentConfirmed = items.filter(item =>
        item.content && item.content.confirmed_by_admin_at
    ).length;
    const sentToPortal = items.filter(item =>
        item.content && item.content.sent_to_portal_at
    ).length;
    const published = items.filter(item =>
        item.live_link_confirmed_at !== null && item.live_link_confirmed_at !== undefined
    ).length;
    const rejected = items.filter(item =>
        item.content && item.content.rejected_at && !item.content.revised_at
    ).length;
    const pendingContent = items.filter(item =>
        item.content && !item.content.confirmed_by_admin_at && !item.content.rejected_at
    ).length;

    const processableItems = items.filter(item =>
        item.content && item.content.confirmed_by_admin_at && item.content.sent_to_portal_at
    ).length;

    const hasPendingItems = pendingContent > 0 || (processableItems > published);

    const hasProcessableItems = processableItems > 0;

    return {
        totalItems,
        hasContent,
        contentConfirmed,
        sentToPortal,
        published,
        rejected,
        pendingContent,
        allHaveContent: hasContent === totalItems,
        allContentConfirmed: contentConfirmed === totalItems,
        allSentToPortal: sentToPortal === totalItems,
        allPublished: published === totalItems,
        hasPendingItems,
        hasProcessableItems
    };
});

const steps = computed(() => {
    const baseSteps = [
        {
            key: 'sent-to-portal',
            icon: 'pi pi-send',
            label: $t('Confirm order'),
            status: props.stepIcons?.['sent-to-portal'] || 'pending',
            route: 'orders.administration',
            enabled: isStepEnabled('sent-to-portal')
        },
        {
            key: 'live-link',
            icon: 'pi pi-link',
            label: getLiveLinkLabel(),
            status: props.stepIcons?.['live-link'] || 'pending',
            route: 'orders.live-link.edit',
            enabled: isStepEnabled('live-link')
        },
        {
            key: 'invoice',
            icon: 'pi pi-file-pdf',
            label: $t('Invoice'),
            status: props.stepIcons?.['invoice'] || 'pending',
            route: 'orders.invoice.show',
            enabled: isStepEnabled('invoice')
        }
    ];

    if (props.order.deferred_payment) {
        baseSteps.push({
            key: 'payment-confirmation',
            icon: 'pi pi-dollar',
            label: $t('Payment Confirmation'),
            status: props.stepIcons?.['payment-confirmation'] || 'pending',
            route: 'orders.payment.confirmation',
            enabled: isStepEnabled('payment-confirmation')
        });
    }

    return baseSteps;
});


function getLiveLinkLabel() {
    const analysis = itemsAnalysis.value;

    if (!analysis.hasProcessableItems) {
        if (analysis.hasPendingItems) {
            return $t('Awaiting content confirmation');
        }
        return $t('Awaiting publication');
    }

    if (analysis.published === analysis.sentToPortal) {
        return $t('All links confirmed') + ` (${analysis.published}/${analysis.sentToPortal})`;
    } else {
        return $t('Links pending') + ` (${analysis.published}/${analysis.sentToPortal})`;
    }
}

function isStepEnabled(stepKey) {
    const analysis = itemsAnalysis.value;
    
    switch(stepKey) {
        case 'sent-to-portal':
            return true;
        case 'live-link':
            return analysis.hasProcessableItems;
        case 'invoice':
            return (props.order.invoice_id !== null && props.order.invoice_id !== undefined) ||
                   analysis.allPublished;
        case 'payment-confirmation':
            return props.order.invoice_id !== null && props.order.invoice_id !== undefined;
        default:
            return false;
    }
}

function handleStepClick(step) {
    if (props.order.status === 'CANCELED') {
        return;
    }

    if (!step.enabled) {
        return;
    }

    if (step.key === 'invoice') {
        router.visit(proxy.route('orders.invoice.show', props.order.id), {
            onStart: () => showLoading($t('Loading...')),
            onFinish: () => hideLoading(),
            onError: () => hideLoading()
        });
        return;
    }

    if (step.key === 'payment-confirmation') {
        router.visit(proxy.route('orders.payment.confirmation', props.order.id), {
            onStart: () => showLoading($t('Loading...')),
            onFinish: () => hideLoading(),
            onError: () => hideLoading()
        });
        return;
    }

    if (step.route && proxy.route) {
        router.visit(proxy.route(step.route, props.order.id), {
            onStart: () => showLoading($t('Loading...')),
            onFinish: () => hideLoading(),
            onError: () => hideLoading()
        });
    }
}

function getStepClass(step) {
    if (props.order.status === 'CANCELED') {
        return 'text-gray-400 cursor-not-allowed opacity-50';
    }

    if (!step.enabled || step.status === 'disabled') {
        return 'text-gray-300 cursor-not-allowed';
    }

    if (step.status === 'completed') {
        return 'text-green-600 hover:text-green-700 cursor-pointer';
    }

    const currentStatus = props.order.status;
    const isCurrentStep = isCurrentStepForStatus(step.key, currentStatus);

    if (isCurrentStep) {
        return 'text-blue-600 hover:text-blue-700 cursor-pointer';
    }

    switch(step.status) {
        case 'in-progress':
            return 'text-blue-600 hover:text-blue-700 cursor-pointer';
        case 'pending':
            return 'text-gray-400 hover:text-gray-500 cursor-pointer';
        default:
            return 'text-gray-400 hover:text-gray-500 cursor-pointer';
    }
}

function isCurrentStepForStatus(stepKey, status) {
    const analysis = itemsAnalysis.value;

    switch(stepKey) {
        case 'sent-to-portal':
            return !analysis.allSentToPortal;
        case 'live-link':
            return analysis.hasProcessableItems && analysis.published < analysis.sentToPortal;
        case 'invoice':
            if (props.order.deferred_payment) {
                return status === 'INVOICE' || (analysis.allPublished && !props.order.invoice_id);
            }
            return status === 'INVOICE' || (analysis.allPublished && status !== 'COMPLETED');
        case 'payment-confirmation':
            return props.order.deferred_payment && props.order.invoice_id && !props.order.payment_date && status === 'WAITING';
        default:
            return false;
    }
}
</script>

<template>
    <div class="flex items-center gap-2">
        <button
            v-for="step in steps"
            :key="step.key"
            @click="handleStepClick(step)"
            :class="getStepClass(step)"
            class="transition-colors duration-200"
            v-tooltip.top="step.label"
            :disabled="!step.enabled"
        >
            <i :class="step.icon" class="text-xl"></i>
        </button>
    </div>
</template>
